import Link from 'next/link';
import {
  ArrowLeft,
  Clock,
  DollarSign,
  Building2,
  Target,
  AlertTriangle,
  BarChart3,
  Map,
} from 'lucide-react';
import type { PlaybookDetail } from '@sizzlenz/shared';
import { Markdown } from '@/components/Markdown';

interface PlaybookDetailViewProps {
  playbook: PlaybookDetail;
  /** When true, skips the header (title/subtitle/tags) and removes outer padding — used when embedded inside ContentDetailView */
  embedded?: boolean;
}

function PlaybookSidebar({ playbook }: { playbook: PlaybookDetail }) {
  return (
    <div className="p-8 rounded-2xl bg-white border border-gray-200 shadow-sm sticky top-24">
      <h4 className="text-lg font-bold mb-6 text-gray-900">Summary</h4>
      <div className="space-y-6">
        {/* Duration */}
        <div className="space-y-2">
          <span className="text-[10px] font-bold text-gray-500 uppercase tracking-widest block">
            Duration
          </span>
          <div className="flex items-center gap-2 text-gray-700">
            <Clock className="w-4 h-4 text-primary-600" />
            <span className="text-sm font-bold">{playbook.timeline.length} Key Milestones</span>
          </div>
        </div>

        {/* Effort */}
        <div className="space-y-2">
          <span className="text-[10px] font-bold text-gray-500 uppercase tracking-widest block">
            Effort
          </span>
          <div className="flex items-center gap-2 text-gray-700">
            <Clock className="w-4 h-4 text-primary-600" />
            <span className="text-sm font-bold">{playbook.effort}</span>
          </div>
        </div>

        {/* Budget */}
        <div className="space-y-2">
          <span className="text-[10px] font-bold text-gray-500 uppercase tracking-widest block">
            Budget
          </span>
          <div className="flex items-center gap-2 text-gray-700">
            <DollarSign className="w-4 h-4 text-primary-600" />
            <span className="text-sm font-bold">{playbook.budget}</span>
          </div>
        </div>

        {/* Business Types */}
        <div className="space-y-3">
          <span className="text-[10px] font-bold text-gray-500 uppercase tracking-widest flex items-center gap-2">
            <Building2 className="w-3 h-3" /> Best For
          </span>
          <div className="flex flex-wrap gap-1.5">
            {playbook.businessTypes.map((type) => (
              <span
                key={type}
                className="px-2 py-1 rounded-lg bg-gray-50 border border-gray-100 text-[10px] text-gray-700 font-bold"
              >
                {type}
              </span>
            ))}
          </div>
        </div>

        {/* Outcomes */}
        <div className="space-y-3">
          <span className="text-[10px] font-bold text-gray-500 uppercase tracking-widest flex items-center gap-2">
            <Target className="w-3 h-3" /> Outcomes ({playbook.outcomes.length})
          </span>
          <div className="flex flex-wrap gap-1.5">
            {playbook.outcomes.map((outcome) => (
              <span
                key={outcome}
                className="px-2 py-1 rounded-lg bg-primary-50 border border-primary-100 text-[10px] text-primary-700 font-bold"
              >
                {outcome}
              </span>
            ))}
          </div>
        </div>

        {/* KPIs */}
        {playbook.kpis && playbook.kpis.length > 0 && (
          <div className="space-y-3">
            <span className="text-[10px] font-bold text-gray-500 uppercase tracking-widest flex items-center gap-2">
              <BarChart3 className="w-3 h-3" /> KPIs ({playbook.kpis.length})
            </span>
            <div className="flex flex-wrap gap-1.5">
              {playbook.kpis.map((kpi) => (
                <span
                  key={kpi}
                  className="px-2 py-1 rounded-lg bg-green-50 border border-green-100 text-[10px] text-green-700 font-bold"
                >
                  {kpi}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function PlaybookTimeline({ playbook }: { playbook: PlaybookDetail }) {
  return (
    <section className="space-y-8">
      <h2 className="flex items-center gap-3 text-2xl font-bold text-gray-900">
        <Map className="w-6 h-6 text-primary-600" />
        The Timeline & Decision Moments
      </h2>

      <div className="relative pl-8 md:pl-12 space-y-10 before:absolute before:left-[11px] md:before:left-[15px] before:top-4 before:bottom-4 before:w-px before:bg-gradient-to-b before:from-primary-600 before:via-gray-200 before:to-transparent">
        {playbook.timeline.map((milestone) => (
          <div key={milestone.id} className="relative group">
            <div
              className={`absolute -left-[37px] md:-left-[45px] top-1 w-5 h-5 md:w-7 md:h-7 rounded-full border-4 border-white z-10 ${
                milestone.isDecisionPoint ? 'bg-amber-500' : 'bg-primary-600'
              }`}
            />

            <div className="p-6 md:p-8 rounded-2xl bg-white border border-gray-200 hover:border-primary-200 transition-all space-y-5 shadow-sm hover:shadow-md">
              <div>
                <div className="flex items-center gap-3 mb-3">
                  <span className="text-xs font-bold text-primary-600 uppercase tracking-widest">
                    {milestone.timeframe}
                  </span>
                  {milestone.isDecisionPoint && (
                    <span className="px-2 py-0.5 rounded-md bg-amber-50 border border-amber-200 text-amber-600 text-[10px] font-bold uppercase tracking-widest">
                      Decision Point
                    </span>
                  )}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{milestone.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed font-medium">
                  {milestone.description}
                </p>
              </div>

              {/* Related items */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-5 border-t border-gray-100">
                {milestone.relatedActivities.length > 0 && (
                  <div className="space-y-2">
                    <span className="text-[10px] font-bold text-gray-500 uppercase tracking-widest flex items-center gap-2">
                      <Target className="w-3 h-3" /> Activities
                    </span>
                    <div className="flex flex-wrap gap-1.5">
                      {milestone.relatedActivities.map((activity) => (
                        <span
                          key={activity}
                          className="px-2.5 py-1 rounded-lg bg-gray-50 border border-gray-100 text-xs text-gray-700 font-bold"
                        >
                          {activity}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {milestone.relatedTemplates.length > 0 && (
                  <div className="space-y-2">
                    <span className="text-[10px] font-bold text-gray-500 uppercase tracking-widest flex items-center gap-2">
                      <Map className="w-3 h-3" /> Templates
                    </span>
                    <div className="flex flex-wrap gap-1.5">
                      {milestone.relatedTemplates.map((template) => (
                        <span
                          key={template}
                          className="px-2.5 py-1 rounded-lg bg-primary-50 border border-primary-100 text-xs text-primary-700 font-bold"
                        >
                          {template}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {milestone.relatedCaseStudies.length > 0 && (
                  <div className="space-y-2">
                    <span className="text-[10px] font-bold text-gray-500 uppercase tracking-widest flex items-center gap-2">
                      <BarChart3 className="w-3 h-3" /> Case Studies
                    </span>
                    <div className="flex flex-wrap gap-1.5">
                      {milestone.relatedCaseStudies.map((study) => (
                        <span
                          key={study}
                          className="px-2.5 py-1 rounded-lg bg-purple-50 border border-purple-100 text-xs text-purple-700 font-bold"
                        >
                          {study}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

export function PlaybookDetailView({ playbook, embedded = false }: PlaybookDetailViewProps) {
  return (
    <div
      className={
        embedded ? 'space-y-12' : 'max-w-5xl mx-auto space-y-12 pb-24 px-4 sm:px-6 lg:px-8 pt-8'
      }
    >
      {/* Header - hidden when embedded (ContentDetailView already shows title) */}
      {!embedded && (
        <div className="space-y-6">
          <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-2">
              {playbook.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1 rounded-full bg-primary-50 border border-primary-200 text-primary-600 text-[10px] font-bold uppercase tracking-widest"
                >
                  {tag}
                </span>
              ))}
            </div>
            <h1 className="text-3xl md:text-5xl font-black tracking-tight text-gray-900 leading-tight">
              {playbook.title}
            </h1>
            <p className="text-xl md:text-2xl font-medium text-gray-500">{playbook.subtitle}</p>
          </div>
        </div>
      )}

      {/* Subtitle shown in embedded mode (title/tags handled by parent) */}
      {embedded && playbook.subtitle && (
        <p className="text-xl md:text-2xl font-medium text-gray-500">{playbook.subtitle}</p>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
        {/* Left: Content */}
        <div className="lg:col-span-2 space-y-16">
          <section className="space-y-6">
            <Markdown className="text-lg leading-relaxed font-medium">
              {playbook.description}
            </Markdown>
          </section>

          {/* Mobile Summary */}
          <div className="lg:hidden">
            <PlaybookSidebar playbook={playbook} />
          </div>

          {/* Timeline */}
          <PlaybookTimeline playbook={playbook} />
          {/* Pitfalls */}
          {playbook.pitfalls && playbook.pitfalls.length > 0 && (
            <section className="space-y-4">
              <h2 className="flex items-center gap-3 text-xl font-bold text-gray-900">
                <AlertTriangle className="w-5 h-5 text-amber-500" />
                Common Pitfalls
              </h2>
              <div className="space-y-3">
                {playbook.pitfalls.map((pitfall, i) => (
                  <div
                    key={i}
                    className="flex items-start gap-3 p-4 rounded-xl bg-amber-50 border border-amber-100"
                  >
                    <span className="flex-shrink-0 w-5 h-5 rounded-full bg-amber-200 text-amber-700 flex items-center justify-center text-xs font-bold mt-0.5">
                      {i + 1}
                    </span>
                    <p className="text-sm text-amber-900 font-medium">{pitfall}</p>
                  </div>
                ))}
              </div>
            </section>
          )}
        </div>

        {/* Right: Sidebar - Desktop only */}
        <div className="hidden lg:block">
          <PlaybookSidebar playbook={playbook} />
        </div>
      </div>
    </div>
  );
}
